/**
 * NumberInput Component
 *
 * Number input with custom increment/decrement buttons that work properly in dark mode.
 * Replaces native browser spinners with styled controls.
 */

import clsx from 'clsx';
import React, { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Input label (renders above input) */
  label?: string;
  /** Error message (renders below input with red styling) */
  error?: string | null;
  /** Helper text (renders below input with muted styling) */
  helperText?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Input style variant */
  variant?: 'default' | 'filled';
  /** Left icon element (renders inside input on left) */
  leftIcon?: React.ReactNode;
  /** Additional class name for the container */
  containerClassName?: string;
  /** Additional class name for the label */
  labelClassName?: string;
  /** Additional class name for the input */
  inputClassName?: string;
}

const sizeClasses = {
  sm: 'py-1 px-2 text-sm h-8',
  md: 'py-2 px-3 text-base h-9',
  lg: 'py-2.5 px-4 text-base h-10',
};

const variantClasses = {
  default: 'bg-bg',
  filled: 'bg-surface',
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput({
  label,
  error,
  helperText,
  size = 'md',
  variant = 'default',
  leftIcon,
  containerClassName,
  labelClassName,
  inputClassName,
  className,
  id,
  step = 1,
  min,
  max,
  value,
  onChange,
  disabled,
  readOnly,
  ...inputProps
}, ref) {
  const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;

  const handleIncrement = () => {
    if (disabled || readOnly) return;
    const currentValue = parseFloat(value?.toString() || '0') || 0;
    const stepValue = parseFloat(step?.toString() || '1');
    const newValue = currentValue + stepValue;
    if (max === undefined || newValue <= parseFloat(max.toString())) {
      onChange?.({ target: { value: newValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDecrement = () => {
    if (disabled || readOnly) return;
    const currentValue = parseFloat(value?.toString() || '0') || 0;
    const stepValue = parseFloat(step?.toString() || '1');
    const newValue = currentValue - stepValue;
    if (min === undefined || newValue >= parseFloat(min.toString())) {
      onChange?.({ target: { value: newValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div className={clsx('flex flex-col', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className={clsx(
            'block text-xs font-bold text-text-muted mb-2 uppercase tracking-[0.5px]',
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      <div className="relative rounded-sm">
        {leftIcon && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          type="number"
          step={step}
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          className={clsx(
            // Base styles
            'w-full border rounded-sm text-text box-border transition-all duration-fast',
            // Hide native spinners
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0',
            // Placeholder styles
            'placeholder:text-sm placeholder:font-normal placeholder:text-text-muted',
            // Focus styles
            'focus:outline-none focus:border-primary focus:shadow-[var(--focus-ring-blue-sm)] dark:focus:shadow-[var(--focus-ring-primary)]',
            // Size variant
            sizeClasses[size],
            // Background variant
            variantClasses[variant],
            // Error state
            error ? 'border-danger' : 'border-border',
            // Left padding when icon is present
            leftIcon && 'pl-8',
            // Right padding for buttons
            'pr-7',
            // Custom classes
            inputClassName,
            className
          )}
          {...inputProps}
        />
        {/* Custom spinner buttons */}
        <div className="absolute right-0 top-px bottom-px flex flex-row md:flex-col border-l border-border">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled || readOnly}
            className="flex-1 px-2 text-text-muted hover:text-text hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-xs leading-none md:border-t md:border-l-0 border-l border-border md:order-2"
            tabIndex={-1}
          >
            ▼
          </button>
          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled || readOnly}
            className="flex-1 px-2 text-text-muted hover:text-text hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-xs leading-none md:order-1"
            tabIndex={-1}
          >
            ▲
          </button>
        </div>
      </div>
      {error && (
        <div className="mt-1 text-xs text-danger bg-red-50 dark:bg-red-900/20 py-1 px-2 rounded-sm border border-danger">
          {error}
        </div>
      )}
      {helperText && !error && (
        <div className="mt-1 text-xs text-text-muted">
          {helperText}
        </div>
      )}
    </div>
  );
});
